import { useEffect, useState } from "react";
import "chart.js/auto";
import { Doughnut } from "react-chartjs-2";

import "./styles.scss";

// Building blocks of the dashboard pages: cards, KPI tiles, rank lists and a
// doughnut with its legend, plus the theme colours Chart.js needs.

const readThemeColors = () => {
  const styles = getComputedStyle(document.documentElement);
  return {
    text: styles.getPropertyValue("--dashboard-text-color").trim() || "#a6b0cf",
    grid: styles.getPropertyValue("--dashboard-table-row-border").trim() || "#565d73",
    card: styles.getPropertyValue("--dashboard-card-color").trim() || "#2a3042",
  };
};

// Chart.js draws on a canvas, so its colours are read from the theme variables
// and read again when the theme switches.
export const useThemeColors = () => {
  const [colors, setColors] = useState(readThemeColors);
  useEffect(() => {
    const observer = new MutationObserver(() => setColors(readThemeColors()));
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });
    return () => observer.disconnect();
  }, []);
  return colors;
};

export const percent = (part, total) => (total ? ((part / total) * 100).toFixed(1) : "0.0");

export const Widget = ({ title, subtitle, span = 4, action, children, className = "" }) => (
  <section className={`mgw-dashboard__item mgw-dashboard__item--span-${span} ${className}`}>
    {(title || action) && (
      <header className="mgw-dashboard__item__header">
        <div>
          <h2>{title}</h2>
          {subtitle && <p className="mgw-dashboard__item__subtitle">{subtitle}</p>}
        </div>
        {action}
      </header>
    )}
    <div className="mgw-dashboard__item__body">{children}</div>
  </section>
);

export const NoData = ({ text = "No data" }) => <p className="mgw-dashboard__no-data">{text}</p>;

export const KpiTile = ({ label, value, hint, tone = "default" }) => (
  <div className={`mgw-kpi mgw-kpi--${tone}`}>
    <span className="mgw-kpi__label">{label}</span>
    <span className="mgw-kpi__value">{value}</span>
    {hint && <span className="mgw-kpi__hint">{hint}</span>}
  </div>
);

// Top-N list with a proportional bar behind each count
export const RankList = ({ items, color, emptyText, onSelect }) => {
  if (!items?.length) return <NoData text={emptyText} />;
  const max = Math.max(...items.map((item) => item.count));
  return (
    <ul className="mgw-rank">
      {items.map((item) => (
        <li key={item.name} className="mgw-rank__row" title={`${item.name}: ${item.count}`}>
          {onSelect ? (
            <button
              type="button"
              className="mgw-rank__name mgw-rank__name--action"
              onClick={() => onSelect(item)}
            >
              {item.name}
            </button>
          ) : (
            <span className="mgw-rank__name">{item.name}</span>
          )}
          <span className="mgw-rank__count">{item.count.toLocaleString()}</span>
          <span className="mgw-rank__track">
            <span
              className="mgw-rank__bar"
              style={{ width: `${(item.count / max) * 100}%`, backgroundColor: color }}
            />
          </span>
        </li>
      ))}
    </ul>
  );
};

export const ChartLegend = ({ items }) => {
  const total = items.reduce((sum, item) => sum + item.value, 0);
  return (
    <ul className="mgw-legend">
      {items.map((item) => (
        <li key={item.label}>
          <span className="mgw-legend__swatch" style={{ backgroundColor: item.color }} />
          <span className="mgw-legend__label">{item.label}</span>
          <span className="mgw-legend__value">{item.value.toLocaleString()}</span>
          {item.showPercent !== false && (
            <span className="mgw-legend__percent">{percent(item.value, total)}%</span>
          )}
        </li>
      ))}
    </ul>
  );
};

export const DoughnutWithLegend = ({ items, centerLabel, centerValue }) => {
  const visible = items.filter((item) => item.inChart !== false);
  const data = {
    labels: visible.map((item) => item.label),
    datasets: [
      {
        data: visible.map((item) => item.value),
        backgroundColor: visible.map((item) => item.color),
        borderWidth: 0,
      },
    ],
  };
  return (
    <div className="mgw-doughnut">
      <div className="mgw-doughnut__chart">
        <Doughnut
          data={data}
          options={{
            maintainAspectRatio: false,
            cutout: "68%",
            plugins: { legend: { display: false } },
          }}
        />
        <div className="mgw-doughnut__center">
          <span>{centerValue}</span>
          <small>{centerLabel}</small>
        </div>
      </div>
      <ChartLegend items={items} />
    </div>
  );
};
